import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AuthenticationContext } from './AuthenticationProvider';
import { ProjectResponse } from './types/ProjectResponse';
import { b64toBlob, getTranslation } from '../utility';
import { useTranslation } from 'react-i18next';
import { ResourcesResponse } from './types/ResourcesResponse';
import moment from 'moment/moment';
import { ResourceType } from './types/Resource';
import { getProject, getProjectIndex, getProjectResources, saveProjectImage } from './api';
import { ActionCableContext } from './ActionCableProvider';
import { trackPromise } from 'react-promise-tracker';

export interface IProjectActivity {
  parentName?: string;
  resourceName: string;
  activityType: 'check_out' | 'check_in' | 'create' | 'destroy' | 'update';
  activityMessage: string;
  userFirstName: string;
  dateTime: string;
}

export interface IOpenResources {
  parentName?: string;
  resourceName: string;
  resourceType: ResourceType;
  userFirstName: string;
  dateTime: string;
}

export interface IProjectOverview {
  projectId: number | undefined;
  projectName: string | undefined;
  projectDescription: string | undefined;
  projectImage: string | undefined;
  recentActivity: IProjectActivity[];
  openResources: IOpenResources[];
}

export interface IProjectOverviewContext {
  projectData: IProjectOverview | undefined;
  onFetch(): void;
  onSaveProjectImage(_projectId: number, _imageURI: string): void;
}

export interface IProjectOverviewProviderProps {
  children: any;
}

const ProjectOverviewContext = React.createContext<IProjectOverviewContext>({
  projectData: undefined,
  onFetch(): void {},
  onSaveProjectImage(_projectId: number, _imageURI: string): void {},
});

const ProjectOverviewProvider = (props: IProjectOverviewProviderProps) => {
  const { children } = props;

  const { id } = useParams();
  const { isAuthenticated, token } = useContext(AuthenticationContext);
  const { onSubscribe, onClearMessages, messages, available } =
    useContext(ActionCableContext);
  const { i18n } = useTranslation();

  const [projectData, setProjectData] = useState<
    IProjectOverview | undefined
  >();
  const [fetch, setFetch] = useState(true);
  const [subscribed, setSubscribed] = useState<boolean>(false);

  const authenticated = useCallback(() => {
    return token && isAuthenticated();
  }, [isAuthenticated, token]);

  const language = useCallback(() => {
    return i18n.language as 'en' | 'fr';
  }, [i18n]);

  useEffect(() => {
    if (id && !subscribed && available) {
      onSubscribe(Number(id));
      setSubscribed(true);
    }
  }, [projectData, subscribed, onSubscribe, id, available]);

  useEffect(() => {
    if (messages[Number(id)] && messages[Number(id)].length > 0) {
      setFetch(true);
      onClearMessages(Number(id));
    }
  }, [messages, id, onClearMessages]);

  useEffect(() => {
    if (authenticated() && fetch && token) {
      let obj: IProjectOverview = {
        projectId: id ? parseInt(id as string) : 0,
        projectName: '',
        projectDescription: '',
        projectImage: '/default-image.png',
        recentActivity: [],
        openResources: [],
      };
      let projectData: ProjectResponse, resourcesData: ResourcesResponse;
      trackPromise(getProject(Number(id), token))
        .then((data) => {
          projectData = data as ProjectResponse;
          trackPromise(getProjectResources(Number(id), token)).then((data) => {
            resourcesData = data as ResourcesResponse;

            if (projectData?.project) {
              obj.projectName = getTranslation(
                language(),
                projectData.project.name
              );
              obj.projectDescription = getTranslation(
                language(),
                projectData.project.description
              );

              obj.projectImage =
                projectData.project.image_url || '/default-image.png';
            }

            if (resourcesData?.resources) {
              resourcesData.resources.forEach((resource) => {
                if (
                  moment(resource.last_action.created_at).isAfter(
                    moment().subtract(1, 'week')
                  )
                ) {
                  obj.recentActivity.push({
                    resourceName: resource.local_id,
                    parentName: resource.parent_resource ? resource.parent_resource.local_id : undefined,
                    activityType: resource.last_action.action_type,
                    activityMessage: resource.last_action.action_message,
                    userFirstName: resource.last_action.user
                      ?.first_name as string,
                    dateTime: resource.last_action.created_at,
                  });
                }
                if (resource.last_action.action_type === 'check_out') {
                  obj.openResources.push({
                    resourceName: resource.local_id,
                    parentName: resource.parent_resource ? resource.parent_resource.local_id : undefined,
                    resourceType: resource.resource_type,
                    userFirstName: resource.last_action.user
                      ?.first_name as string,
                    dateTime: resource.last_action.created_at,
                  });
                }
              });
            }

            setFetch(false);
            setProjectData(obj);
          });
        })
        .catch((error: any) => {
          console.log(error);
        });
    }
  }, [token, authenticated, id, language, fetch]);

  const onFetch = () => {
    setFetch(true);
  };

  const onSaveProjectImage = (projectId: number, imageURI: string) => {
    if (isAuthenticated() && token) {
      const blob = b64toBlob(imageURI);

      let formData = new FormData();
      formData.append('image', blob);

      trackPromise(saveProjectImage(projectId, formData, token)).then(() =>
        trackPromise(getProjectIndex(token)).then((data) => {
          if (data) {
            setFetch(true);
          }
        })
      );
    }
  };

  return (
    <ProjectOverviewContext.Provider
      value={{
        projectData,
        onFetch,
        onSaveProjectImage,
      }}
    >
      {children}
    </ProjectOverviewContext.Provider>
  );
};

export { ProjectOverviewContext, ProjectOverviewProvider };
